import { DataGrid, StatusBadge, type Severity } from '@ijoin/components';
import { useGetAdminDbInsightsTable } from '@ijoin/hooks';
import { ArrowBack } from '@mui/icons-material';
import {
  Alert,
  Button,
  Card,
  CardContent,
  Stack,
  Typography
} from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { createFileRoute, useNavigate } from '@tanstack/react-router';

type Definition = {
  collation: null | string;
  columns: Record<string, ColumnRow>;
  engine: null | string;
  foreignKeys: Record<string, ForeignKeyRow>;
  indexes: Record<string, IndexRow>;
};

type ColumnRow = {
  collation: null | string;
  default: null | string;
  extra: string;
  nullable: boolean;
  position: number;
  type: string;
};

type IndexRow = {
  columns: string[];
  subParts: (null | number)[];
  type: string;
  unique: boolean;
};

type ForeignKeyRow = {
  columns: string[];
  onDelete: string;
  onUpdate: string;
  referencedColumns: string[];
  referencedTable: string;
};

type Status = 'differs' | 'extra' | 'matches' | 'missing';

const STATUS_BADGES: Record<Status, Severity> = {
  differs: 'warning',
  extra: 'info',
  matches: 'success',
  missing: 'error'
};

const STATUS_LABELS: Record<Status, string> = {
  differs: 'Differs',
  extra: 'Not in baseline',
  matches: 'Matches',
  missing: 'Missing'
};

const StatusCell = (props: { status: Status }) => (
  <StatusBadge severity={STATUS_BADGES[props.status]}>
    {STATUS_LABELS[props.status]}
  </StatusBadge>
);

/* one row set keyed by name, tagged with how the two sides compare */
const mergeRows = <T,>(params: {
  baseline: Record<string, T> | undefined;
  isEqual: (a: T, b: T) => boolean;
  live: Record<string, T> | undefined;
}) =>
  [
    ...new Set([
      ...Object.keys(params.baseline ?? {}),
      ...Object.keys(params.live ?? {})
    ])
  ]
    .sort()
    .map(name => {
      const expected = params.baseline?.[name];
      const found = params.live?.[name];

      const status: Status = !found
        ? 'missing'
        : !expected
          ? 'extra'
          : params.isEqual(expected, found)
            ? 'matches'
            : 'differs';

      return { expected, found, name, status };
    });

const describeColumn = (column?: ColumnRow) =>
  column
    ? [
        column.type,
        column.nullable ? 'NULL' : 'NOT NULL',
        column.collation ?? '',
        column.default === null ? '' : `DEFAULT ${column.default}`,
        column.extra
      ]
        .filter(Boolean)
        .join(' ')
    : '';

const describeIndex = (index?: IndexRow) =>
  index ? `${index.unique ? 'UNIQUE ' : ''}(${index.columns.join(', ')})` : '';

const describeForeignKey = (key?: ForeignKeyRow) =>
  key
    ? `(${key.columns.join(', ')}) -> ${key.referencedTable}(${key.referencedColumns.join(', ')}) ON DELETE ${key.onDelete} ON UPDATE ${key.onUpdate}`
    : '';

const comparisonColumns = <T,>(
  describe: (value?: T) => string
): GridColDef<{ expected?: T; found?: T; name: string; status: Status }>[] => [
  { field: 'name', flex: 1.2, headerName: 'Name' },
  {
    field: 'status',
    headerName: 'Status',
    renderCell: params => <StatusCell status={params.row.status} />,
    width: 190
  },
  {
    field: 'expected',
    flex: 2,
    headerName: 'Baseline',
    valueGetter: (_value, row) => describe(row.expected)
  },
  {
    field: 'found',
    flex: 2,
    headerName: 'This database',
    valueGetter: (_value, row) => describe(row.found)
  }
];

const HEADER_HEIGHT = 56;
const ROW_HEIGHT = 52;
/* roughly nine rows, past which a wide table pushes everything else off the page */
const MAX_GRID_HEIGHT = 524;

const gridProps = (testId: string) => ({
  disableColumnMenu: true,
  disableRowSelectionOnClick: true,
  getRowId: (row: { name: string }) => row.name,
  hideFooter: true,
  slotProps: {
    root: { 'data-component': 'dataGrid', 'data-testid': testId }
  }
});

/* the grid fills its container, and these stack in a page with no height to
   hand down, so the container has to state one */
const gridHeight = (rowCount: number) =>
  Math.min(
    /* two rows minimum: an empty grid still has to fit the no-records overlay */
    HEADER_HEIGHT + Math.max(rowCount, 2) * ROW_HEIGHT + 2,
    MAX_GRID_HEIGHT
  );

const TableDetailPage = () => {
  const { table } = Route.useParams();
  const navigate = useNavigate();
  const detail = useGetAdminDbInsightsTable(table);

  const baseline = detail.data?.baseline as Definition | null | undefined;
  const live = detail.data?.live as Definition | null | undefined;

  const columnRows = mergeRows<ColumnRow>({
    baseline: baseline?.columns,
    isEqual: (a, b) =>
      a.type === b.type &&
      a.nullable === b.nullable &&
      a.default === b.default &&
      a.collation === b.collation,
    live: live?.columns
  });

  const indexRows = mergeRows<IndexRow>({
    baseline: baseline?.indexes,
    isEqual: (a, b) =>
      a.unique === b.unique && a.columns.join(',') === b.columns.join(','),
    live: live?.indexes
  });

  const foreignKeyRows = mergeRows<ForeignKeyRow>({
    baseline: baseline?.foreignKeys,
    isEqual: (a, b) =>
      a.columns.join(',') === b.columns.join(',') &&
      a.referencedTable === b.referencedTable &&
      a.referencedColumns.join(',') === b.referencedColumns.join(',') &&
      a.onDelete === b.onDelete &&
      a.onUpdate === b.onUpdate,
    live: live?.foreignKeys
  });

  const problems = detail.data?.findings.length ?? 0;

  return (
    <Stack spacing={2} sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
      <Stack alignItems='center' direction='row' spacing={2}>
        <Button
          data-testid='backToDbInsights'
          onClick={() => navigate({ to: '/db-insights/tables' })}
          startIcon={<ArrowBack />}>
          Tables
        </Button>

        <Typography variant='h5'>{table}</Typography>

        {!!detail.data && (
          <span data-testid='tableDriftCount'>
            <StatusBadge severity={problems ? 'error' : 'success'}>
              {problems ? `${problems} differences` : 'Matches the baseline'}
            </StatusBadge>
          </span>
        )}
      </Stack>

      {!!baseline && !live && (
        <Alert data-testid='tableMissingLive' severity='error'>
          This table is in the baseline but missing from this database.
        </Alert>
      )}

      {!baseline && !!live && (
        <Alert data-testid='tableMissingBaseline' severity='info'>
          This table exists here but is not in the baseline, so it was created
          outside of Flyway or the baseline is out of date.
        </Alert>
      )}

      <Card>
        <CardContent>
          <Stack direction='row' spacing={4}>
            <Stack>
              <Typography color='text.secondary' variant='body2'>
                Engine
              </Typography>
              <Typography>{live?.engine ?? baseline?.engine}</Typography>
            </Stack>

            <Stack>
              <Typography color='text.secondary' variant='body2'>
                Collation
              </Typography>
              <Stack alignItems='center' direction='row' spacing={1}>
                <Typography>
                  {live?.collation ?? baseline?.collation}
                </Typography>

                {!!baseline &&
                  !!live &&
                  baseline.collation !== live.collation && (
                    <StatusBadge severity='error'>
                      {`baseline ${baseline.collation ?? 'none'}`}
                    </StatusBadge>
                  )}
              </Stack>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      <Typography variant='h6'>Columns</Typography>
      <Stack sx={{ flexShrink: 0, height: gridHeight(columnRows.length) }}>
        <DataGrid
          {...gridProps('tableColumns')}
          columns={comparisonColumns(describeColumn)}
          loading={detail.isFetching}
          rows={columnRows}
        />
      </Stack>

      <Typography variant='h6'>Indexes</Typography>
      <Stack sx={{ flexShrink: 0, height: gridHeight(indexRows.length) }}>
        <DataGrid
          {...gridProps('tableIndexes')}
          columns={comparisonColumns(describeIndex)}
          loading={detail.isFetching}
          rows={indexRows}
        />
      </Stack>

      <Typography variant='h6'>Foreign Keys</Typography>
      <Stack sx={{ flexShrink: 0, height: gridHeight(foreignKeyRows.length) }}>
        <DataGrid
          {...gridProps('tableForeignKeys')}
          columns={comparisonColumns(describeForeignKey)}
          loading={detail.isFetching}
          rows={foreignKeyRows}
        />
      </Stack>
    </Stack>
  );
};

export const Route = createFileRoute('/db-insights/tables/$table')({
  component: TableDetailPage
});
